import type {
  CompanyId,
  DomainType,
  FieldId,
  ProjectBaseType,
  ProjectDetail,
  ProjectState,
  ProjectType,
} from '@core/core/types'
import type { Nullable, ProjectId, TabId, WellId } from 'common/types'

import { constructClientDispenser } from '../../clientDispenserFactory'
import { axiosInstance } from '../../common'
import { CorrelationSchemeOptionsClient } from '../__generated__/CorrelationSchemeOptions'
import { HttpClient } from '../__generated__/http-client'
import { ProjectClient } from '../__generated__/Project'
import { ProjectOptionsClient } from '../__generated__/ProjectOptions'
import { ProjectWindowClient } from '../__generated__/ProjectWindow'
import { WorkAreaClient } from '../__generated__/WorkArea'
import {
  convertDomainTypes,
  convertProjectInfo,
  convertProjectInfos,
  convertProjectTypes,
} from './converters'

const Clients = {
  CorrelationSchemeOptionsClient,
  ProjectClient,
  ProjectOptionsClient,
  ProjectWindowClient,
  WorkAreaClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

/// projects
export const getAllProjectsApi = (): Promise<ProjectDetail[]> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectGetAllProjects().then(convertProjectInfos)
}

export const getAllProjectTypesApi = (): Promise<ProjectBaseType[]> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectGetProjectTypes().then(convertProjectTypes)
}

export const getAllDomainTypesApi = (): Promise<DomainType[]> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectGetDomainTypes().then(convertDomainTypes)
}

export const getProjectByIdApi = (projectId: ProjectId): Promise<ProjectDetail> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectGetProjectById(projectId).then(convertProjectInfo)
}
export const updateProjectApi = (
  projectId: ProjectId,
  name: string,
  type: ProjectType,
  projectYear: number,
  companyId?: Nullable<CompanyId>,
  fieldId?: Nullable<FieldId>,
): Promise<boolean> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectUpdateProject({
    uid: projectId,
    projectName: name,
    projectType: type,
    projectYear,
    companyUid: companyId,
    fieldUid: fieldId,
  })
}

export const removeProjectApi = (projectId: ProjectId): Promise<boolean> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectDeleteProject({ projectId })
}

export const changeProjectStateApi = (
  projectId: ProjectId,
  newState: ProjectState,
): Promise<boolean> => {
  const projectClient = getClient('ProjectClient')
  return projectClient.projectSetProjectState(projectId, newState)
}

export const createCorrelationSchemeOptionsApi = (
  projectId: ProjectId,
  correlationSchemeId: TabId,
  wellsIds: WellId[],
): Promise<void> => {
  const projectClient = getClient('CorrelationSchemeOptionsClient')
  return projectClient.correlationSchemeOptionsSetWells(projectId, correlationSchemeId, wellsIds)
}

export const getCorrelationSchemeOptionsApi = (
  projectId: ProjectId,
  correlationSchemeId: TabId,
): Promise<string[]> => {
  const projectClient = getClient('CorrelationSchemeOptionsClient')
  return projectClient.correlationSchemeOptionsGetWellIds(projectId, correlationSchemeId)
}
